import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../Context/AuthContext";
import Preloader from "../../shared/PreLoaders";
import TableComponent from "../../utils/TableComponent";
import { Authorization } from "../../Context/Authorization";

const ManageSHG = () => {
  const [shgData, setShgData] = useState([]);
  const navigate = useNavigate();
  const { permissions, selectedJfmcType } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const Api = process.env.REACT_APP_API;

  useEffect(() => {
    if (!selectedJfmcType) return;
    fetchSHG(selectedJfmcType._id);
  }, [selectedJfmcType]);

  const fetchSHG = async (jfmcTypeId) => {
    setLoading(true);
    try {
      const config = Authorization();
      const response = await axios.post(`${Api}/api/shgs/by-jfmc`, { jfmcTypeId }, config);
      const data = response.data.shgs;
      if (Array.isArray(data)) setShgData(data);
    } catch (error) {
      console.error("Error fetching SHGs", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSHG = async (id) => {
    if (!window.confirm("Are you sure you want to delete this Self Help Group?")) return;
    try {
      const config = Authorization();
      await axios.delete(`${Api}/api/shgs/${id}`, config);
      setShgData(shgData.filter((s) => s._id !== id));
      toast.success("Deleted successfully");
    } catch (err) {
      console.error("Error deleting SHG", err);
      toast.error("Failed to delete SHG");
    }
  };

  const columns = [
    { headerName: "SI.No", field: "index", valueGetter: (p) => p.node.rowIndex + 1, width: 70 },
    { headerName: "SHG Name", field: "name" },
    { headerName: "SHG Code", field: "code" },
    { headerName: "JFMC Name", field: "vfcId.vfcName" },
    { headerName: "Type", field: "type" },
    { headerName: "Location", field: "location" },
    { headerName: "Village", field: "village" },
    { headerName: "Panchayath", field: "panchayath" },
    { headerName: "Thaluk", field: "thaluk" },
    { headerName: "Pin Code", field: "pinCode" },
    {
      headerName: "Established",
      field: "dateOfEstablishment",
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString("en-IN") : "",
    },
    { headerName: "Bank", field: "bankName" },
    { headerName: "Branch", field: "branch" },
    { headerName: "A/c No", field: "accountNumber" },
    { headerName: "IFSC", field: "ifscCode" },
    {
      headerName: "Actions",
      field: "action",
      pinned: "right",
      width: 100,
      cellRenderer: (params) => (
        <div style={{ display: "flex", gap: 8, alignItems: "center", height: "100%" }}>
          {permissions.includes("EditSHG") && (
            <button
              title="Edit"
              onClick={() => navigate(`edit/${params.data._id}`)}
              style={{ background: "#E8F5E9", border: "none", borderRadius: 6, padding: "3px 8px", cursor: "pointer", color: "#2E7D32" }}
            >
              <i className="mdi mdi-pencil" style={{ fontSize: 15 }} />
            </button>
          )}
          {permissions.includes("DeleteSHG") && (
            <button
              title="Delete"
              onClick={() => handleDeleteSHG(params.data._id)}
              style={{ background: "#FFF5F5", border: "none", borderRadius: 6, padding: "3px 8px", cursor: "pointer", color: "#E53E3E" }}
            >
              <i className="mdi mdi-delete" style={{ fontSize: 15 }} />
            </button>
          )}
        </div>
      ),
    },
  ];

  if (!selectedJfmcType) {
    return (
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "48px 24px", textAlign: "center" }}>
        <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
          <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>No JFMC Type Selected</div>
        <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar</div>
      </div>
    );
  }

  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* Page Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>Manage SHGs</h4>
          <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
            <span style={{ color: "#A0AEC0" }}>SHG</span>
            <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
            <span style={{ color: "#2E7D32", fontWeight: 600 }}>All SHGs</span>
          </div>
        </div>
        {permissions.includes("AddSHG") && (
          <button
            onClick={() => navigate("/SHG/add")}
            style={{ padding: "9px 20px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 7, boxShadow: "0 2px 8px rgba(46,125,50,0.3)" }}
          >
            <i className="mdi mdi-plus" style={{ fontSize: 16 }} />
            Add SHG
          </button>
        )}
      </div>

      {/* Table Card */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid #EDF2F7", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
          <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
            <i className="mdi mdi-group" />
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Self Help Groups</span>
          <span style={{ marginLeft: "auto", background: "rgba(255,255,255,0.2)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 50 }}>
            {shgData.length} records
          </span>
        </div>
        <div style={{ padding: 16 }}>
          <TableComponent data={shgData} columns={columns} />
        </div>
      </div>
    </div>
  );
};

export default ManageSHG;
